import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { Delete as DeleteIcon, Replay as ReplayIcon } from '@mui/icons-material';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../index';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

/* Helper to build chips from search params */
function buildParamChips(params) {
  if (!params) return [];
  const chips = [];

  if (params.make) chips.push({ label: params.make, variant: 'filled' });
  if (params.model) chips.push({ label: params.model, variant: 'filled' });
  if (params.yearMin || params.yearMax) {
    chips.push({ label: `${params.yearMin || '?'}–${params.yearMax || 'Now'}`, variant: 'outlined' });
  }
  if (params.priceMax) {
    chips.push({ label: `≤ $${Number(params.priceMax).toLocaleString()}`, variant: 'outlined' });
  }
  if (params.maxMileage) {
    chips.push({ label: `≤ ${Number(params.maxMileage).toLocaleString()} mi`, variant: 'outlined' });
  }
  if (params.zipCode) {
    chips.push({ label: `📍 ${params.zipCode}${params.radius ? ` (${params.radius} mi)` : ''}`, variant: 'outlined' });
  }
  if (params.bodyType) chips.push({ label: params.bodyType, variant: 'outlined' });
  if (params.exteriorColor?.length) {
    params.exteriorColor.forEach((c) => chips.push({ label: c, variant: 'outlined' }));
  }
  if (params.engineType?.length) {
    params.engineType.forEach((e) => chips.push({ label: e, variant: 'outlined' }));
  }
  if (params.drivetrain?.length) {
    params.drivetrain.forEach((d) => chips.push({ label: d, variant: 'outlined' }));
  }
  if (params.cleanTitle) chips.push({ label: 'Clean Title', variant: 'outlined' });

  return chips;
}

function SavedSearches() {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSearches = async () => {
      try {
        const q = query(
          collection(db, 'saved_searches'),
          where('user_id', '==', user.uid),
          orderBy('created_at', 'desc')
        );
        const snapshot = await getDocs(q);
        setSearches(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error fetching saved searches:', err);
        setError('Failed to load saved searches');
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchSearches();
  }, [user]);

  const handleDelete = async (searchId) => {
    try {
      await deleteDoc(doc(db, 'saved_searches', searchId));
      setSearches((prev) => prev.filter((s) => s.id !== searchId));
    } catch (err) {
      console.error('Error deleting search:', err);
    }
  };

  const handleRerun = (search) => {
    navigate('/search', { state: { filters: search.search_params || search.filters } });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Saved Searches
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {searches.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No saved searches yet. Search for vehicles and save your search criteria.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List>
            {searches.map((search) => {
              const params = search.search_params || search.filters || {};
              const chips = buildParamChips(params);

              return (
                <ListItem key={search.id} divider sx={{ pr: { xs: 12, sm: 14 } }}>
                  <ListItemText
                    primary={
                      chips.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                          {chips.map((chip, i) => (
                            <Chip key={i} label={chip.label} size="small" variant={chip.variant} />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2">{search.name || 'Saved Search'}</Typography>
                      )
                    }
                    secondary={
                      search.created_at
                        ? `Saved on ${new Date(search.created_at.seconds * 1000).toLocaleDateString()}`
                        : 'Recently saved'
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleRerun(search)} sx={{ mr: 0.5 }}>
                      <ReplayIcon />
                    </IconButton>
                    <IconButton edge="end" onClick={() => handleDelete(search.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}
    </Box>
  );
}

export default SavedSearches;
